<template>
  <div class="step__block-field step__block-field--bottom">
    <div class="step__block-field-block">
      <h3>Installation billable for client:</h3>

      <PriceValue
        :price="totalInstallationSum"
        class="step__block-field-price"
      />
    </div>
    <p v-if="isKompasDouble">*Upcharge K=1.5 for Double Glazing Installation</p>
  </div>
</template>

<script setup lang="ts">
import { computed, watch } from "vue";
import { Pricing } from "@/app/domain";
import { PriceValue } from "@/shared";

import { useInstallationStore } from "@/stores/new/installation.store";
import { useNewEstimateStore } from "@/stores/new/estimateStore";
import { PricingService } from "@/app/services/pricing.service";
import { isKompasDlb } from "@/app/predicates/systems.predicates";

const newInstallationStore = useInstallationStore();
const estimateStore = useNewEstimateStore();
const pricingService = new PricingService();

const estimate = computed(() => estimateStore.estimate);
const dealType = computed(() => estimateStore.estimate.dealTypeCode);
const totalInstallationSum = computed(
  () => newInstallationStore.totalInstallationSum,
);
const isKompasDouble = computed(() =>
  isKompasDlb(estimate.value.installedSystemCode),
);

const calculateInstallationPageTotal = (isHalfInstallation: boolean) => {
  const halfPriceWithDiscount = pricingService.calculateHalfInstallation();
  const formattedHalfPriceWithDiscount = new Pricing(
    halfPriceWithDiscount.price,
    halfPriceWithDiscount.priceB2C,
    halfPriceWithDiscount.priceB2B,
  );

  const halfInstallationSum = isHalfInstallation
    ? formattedHalfPriceWithDiscount.getSystemPrice(dealType.value).getValue()
    : 0;

  newInstallationStore.halfInstallationSum = halfInstallationSum;

  const installationTotal = pricingService.calculateInstallationTotalSum();
  const formattedInstallationTotal = new Pricing(
    installationTotal.price,
    installationTotal.priceB2C,
    installationTotal.priceB2B,
  );
  newInstallationStore.totalInstallationSum = formattedInstallationTotal;
};

// calculate half installation sum and page total
watch(
  () => [
    estimate.value.isHalfInstallation,
    newInstallationStore.priceWithDiscount,
    estimate.value.installation,
    newInstallationStore.isOverchargeSelected,
    newInstallationStore.overChargeSum,
  ],
  ([isHalf, discountPrice, isInstallation]) => {
    const isHalfInstallation = isHalf;
    if (!isInstallation) {
      newInstallationStore.totalInstallationSum = new Pricing(0, 0, 0);
      return;
    }
    calculateInstallationPageTotal(isHalfInstallation);
  },
  {
    deep: true,
  },
);

watch(
  () => newInstallationStore.halfInstallationSum,
  (value) => {
    estimate.value.halfInstallation = value;
  },
  {
    deep: true,
  },
);
</script>

<style scoped>
.step__block-field {
  display: flex;
  justify-content: space-between;
  align-items: center;

  padding: 15px 0;
  border-top: 1px solid #d2d8e3;
  border-bottom: 1px solid #d2d8e3;
}

.step__block-field-block {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.step__block-field.step__block-field--bottom {
  flex-direction: column;
  align-items: flex-start;
  margin-top: 32px;
  padding-top: 24px;
  padding-bottom: 0;
  border-bottom: none;
}

.step__block-field h3 {
  color: #404d63;
  font-family: Gilroy;
  font-size: 18px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
  text-transform: uppercase;
}

.step__block-field p {
  margin-top: 4px;
  color: #052141;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
}

.step__block-field-price {
  color: #404d63;
  font-size: 18px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
  text-transform: uppercase;
}
</style>
